// Constants and initial/default values
import I_EmployeeNameAndID from "src/common_interfaces/I_EmployeeNameAndID";
import approvalReferenceBoilerplate from "./constants/ApprovalReferenceBoilerplate";

// CRUD operations
import CreateApprovalWorkflow from "./crud/CreateApprovalWorkflow";
import getApprovalReference from "./crud/GetApprovalsReference";
import GetApprovalRecordTypes from "./crud/GetApprovalRecordTypes";

// Interfaces
import { I_ApprovalHistory, I_ApprovalRecordType, I_ApprovalStatusCounts, I_CreateApprovalWorkflow } from "./interfaces";

// Types

import type { T_ApprovalAlias } from "./types";

/**
 * Centralized service for managing approval-related data and actions.
 * Holds reference data used throughout the approval system and exposes methods
 * for initializing reference data and creating workflows.
 */

const approvalService = {
  // Time format for dates
  timeFormat: "MM/DD/YY h:mm a",
  // Flag to track whether reference data has been loaded
  initialized: false,

  // Approval-related lookup data (e.g., status types, chains, templates)
  reference: approvalReferenceBoilerplate,

  // Cached record types (e.g., Lease, SafetyEvent)
  recordTypes: [] as I_ApprovalRecordType[],

  //map for quick employee name lookup
  employeeMap: new Map<number, I_EmployeeNameAndID>(),

  /**
   * Creates a new approval workflow using the provided payload.
   * Logs errors if the creation fails.
   */
  creatNewWF: async (payload: I_CreateApprovalWorkflow) => {
    try {
      await CreateApprovalWorkflow(payload);
    } catch (error) {
      console.error("Failed to create approval workflow:", error);
    }
  },
  // Quick lookups from internal maps
  retrieveEmployeeFromMap: (EmployeeID: number): I_EmployeeNameAndID | null =>
    approvalService.employeeMap.get(EmployeeID) ?? null,
  // Build lookup maps from reference data
  buildEmployeeMap: (employees: I_EmployeeNameAndID[]): void => {
    approvalService.employeeMap.clear();
    employees.forEach((emp) =>
      approvalService.employeeMap.set(emp.EmployeeID, emp)
    );
  },
  //pass in any T_ApprovalAlias and I_ApprovalHistory[] to apply aliases
  applyAliasesToHistory: (record: T_ApprovalAlias, history: I_ApprovalHistory[]): I_ApprovalHistory[] => {
    return history.map(item => {
      const recordItem = record[item.ApprovalStatusTypeId ?? -1]
      const aliasedActionName = recordItem ? recordItem.ActionName : item.ActionName
      const aliasedStatusName = recordItem ? recordItem.StatusName : item.StatusName
      return {
        ...item,
        ActionName: aliasedActionName,
        StatusName: aliasedStatusName
      }
    })
  },
  //get a single history item alias
  getAlias: (record: T_ApprovalAlias, item: I_ApprovalStatusCounts) => {
    const recordItem = record[item.ApprovalStatusTypeId]
    return recordItem ? recordItem.StatusName : item.Status
  },
  /**
   * Fetches and caches all approval record types.
   */
  loadRecordTypes: async () => {
    approvalService.recordTypes = await GetApprovalRecordTypes();
  },
  /**
   * Looks up a record type ID by name.
   */
  getRecordTypeId: (name: string): number | null => {
    const rt = approvalService.recordTypes.find((r) => r.Name === name);
    return rt ? rt.RecordTypeId : null;
  },
  /**
   * Initializes the approval service by loading reference data from the API.
   * Can be scoped to a specific record type if a recordTypeId is passed.
   */
  init: async (recordTypeId?: number) => {
    approvalService.reference = await getApprovalReference(recordTypeId);
    approvalService.buildEmployeeMap(approvalService.reference.Employees);
    approvalService.initialized = true;
  },
};

export default approvalService;
